import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Send, Search, Users, Plus, X, Check } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { useCounters } from "../../contexts/CountersContext";

function fmtRelative(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return "ora";
    if (diff < 3600) return `${Math.floor(diff / 60)} min fa`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} ore fa`;
    if (diff < 172800) return `ieri ${d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}`;
    if (diff < 7 * 86400) return d.toLocaleDateString("it-IT", { weekday: "short", hour: "2-digit", minute: "2-digit" });
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
}

function fmtFull(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

function ThreadRow({ active, onClick, title, subtitle, lastAt, unread, logoUrl, dataTestId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={dataTestId}
            className={"lt-thread-row" + (active ? " is-active" : "") + (unread ? " is-unread" : "")}
        >
            {logoUrl !== undefined && (
                <div className="lt-thread-row__logo">
                    {logoUrl
                        ? <img src={logoUrl} alt="" />
                        : <span>{(title || "?").slice(0, 1).toUpperCase()}</span>}
                </div>
            )}
            <div className="lt-thread-row__body">
                <div className="lt-thread-row__title">
                    {unread && <span className="lt-thread-row__dot" />}
                    <span>{title}</span>
                </div>
                {subtitle && <div className="lt-thread-row__subtitle">{subtitle}</div>}
            </div>
            {lastAt && <div className="lt-thread-row__time">{fmtRelative(lastAt)}</div>}
        </button>
    );
}

function ChatMessage({ m, viewerIsAuthor }) {
    const isInternal = m.author?.user_type === "internal";
    return (
        <div className={"lt-chatmsg " + (viewerIsAuthor ? "lt-chatmsg--self" : isInternal ? "lt-chatmsg--internal" : "lt-chatmsg--client")} data-testid={`chatmsg-${m.id}`}>
            <div className="lt-chatmsg__head">
                <span className="lt-chatmsg__author">{m.author?.name || "—"}</span>
                <span className="lt-chatmsg__time">{fmtFull(m.created_at)}</span>
            </div>
            <div className="lt-chatmsg__body">{m.text}</div>
        </div>
    );
}

export default function Comunicazioni() {
    const { refresh: refreshCounters } = useCounters();
    const [index, setIndex] = useState({ internal: [], clients: [], mixed_dms: [] });
    const [search, setSearch] = useState("");
    const [selectedId, setSelectedId] = useState(null);
    const [selectedIsMixed, setSelectedIsMixed] = useState(false);
    const [thread, setThread] = useState(null);
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [me, setMe] = useState(null);
    const [newConvOpen, setNewConvOpen] = useState(false);
    const [recipientSections, setRecipientSections] = useState(null);
    const [convSelected, setConvSelected] = useState([]); // [{user_type, user_id, name}]
    const [convQuery, setConvQuery] = useState("");
    const [creatingConv, setCreatingConv] = useState(false);
    const threadEndRef = useRef(null);

    useEffect(() => {
        api.get("/auth/me").then(({ data }) => setMe(data)).catch(() => {});
    }, []);

    const loadIndex = useCallback(async (preserveSelected = true) => {
        try {
            const [threadsRes, dmRes] = await Promise.all([
                api.get("/internal/threads"),
                api.get("/comunicazioni/dm").catch(() => ({ data: { items: [] } })),
            ]);
            const data = threadsRes.data;
            const adapted = {
                internal: data.team ? [data.team] : (data.internal || []),
                internal_dms: data.internal_dms || [],
                clients: data.clients || [],
                mixed_dms: dmRes.data.items || [],
            };
            setIndex(adapted);
            if (!preserveSelected || !selectedId) {
                const unread = (adapted.clients || []).find((c) => c.internal_unread)
                    || (adapted.internal_dms || []).find((dm) => dm.unread)
                    || (adapted.mixed_dms || []).find((dm) => dm.unread);
                if (unread) {
                    setSelectedId(unread.id);
                    setSelectedIsMixed((adapted.mixed_dms || []).some((d) => d.id === unread.id));
                } else if (adapted.internal?.[0]) {
                    setSelectedId(adapted.internal[0].id);
                    setSelectedIsMixed(false);
                }
            }
        } catch {
            toast.error("Errore caricamento conversazioni");
        }
    }, [selectedId]);

    const loadThread = useCallback(async (id, isMixed) => {
        if (!id) return;
        try {
            const url = isMixed ? `/comunicazioni/dm/${id}` : `/internal/threads/${id}`;
            const { data } = await api.get(url);
            // Normalize mixed DM to have `type='dm'` for downstream rendering
            if (isMixed && !data.type) data.type = "dm";
            setThread(data);
            refreshCounters();
        } catch {
            toast.error("Conversazione non disponibile");
        }
    }, [refreshCounters]);

    useEffect(() => { loadIndex(false); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []); // initial mount
    useEffect(() => { if (selectedId) loadThread(selectedId, selectedIsMixed); }, [selectedId, selectedIsMixed, loadThread]);
    useEffect(() => {
        const id = setInterval(() => { loadIndex(true); }, 30000);
        return () => clearInterval(id);
    }, [loadIndex]);

    useEffect(() => {
        if (threadEndRef.current) threadEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }, [thread?.messages?.length, selectedId]);

    const filteredClients = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return index.clients || [];
        return (index.clients || []).filter((t) => (t.client?.company_name || "").toLowerCase().includes(q));
    }, [index.clients, search]);

    const send = async (e) => {
        e?.preventDefault?.();
        const value = text.trim();
        if (!value || !selectedId || sending) return;
        setSending(true);
        try {
            const url = selectedIsMixed
                ? `/comunicazioni/dm/${selectedId}/messages`
                : `/internal/threads/${selectedId}/messages`;
            await api.post(url, { text: value });
            setText("");
            await loadThread(selectedId, selectedIsMixed);
            loadIndex(true);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Errore invio");
        } finally {
            setSending(false);
        }
    };

    const onComposerKey = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send();
        }
    };

    const internalThread = index.internal?.[0];
    const isInternalSelected = selectedId === internalThread?.id;
    const selectedClient = (index.clients || []).find((c) => c.id === selectedId);

    return (
        <div className="lt-comms" data-testid="comms-page">
            {/* LEFT — threads list */}
            <aside className="lt-comms__list" data-testid="comms-list">
                <div className="lt-comms__search" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ flex: 1, position: "relative" }}>
                        <Search size={14} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#8A8F88" }} />
                        <input
                            type="search"
                            placeholder="Cerca conversazione o cliente…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            data-testid="comms-search"
                        />
                    </div>
                    <button
                        type="button"
                        className="lt-btn-secondary"
                        onClick={async () => {
                            setNewConvOpen(true);
                            if (!recipientSections) {
                                try {
                                    const { data } = await api.get("/comunicazioni/recipients");
                                    setRecipientSections(data.sections || []);
                                } catch { setRecipientSections([]); }
                            }
                        }}
                        title="Nuova conversazione"
                        aria-label="Nuova conversazione"
                        style={{ width: 40, height: 40, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                        data-testid="comms-new-conv-btn"
                    >
                        <Plus size={18} />
                    </button>
                </div>

                <div className="lt-comms__section">
                    <div className="lt-comms__section-h">Team ENERLEFT</div>
                    {internalThread && (
                        <ThreadRow
                            active={isInternalSelected}
                            onClick={() => { setSelectedId(internalThread.id); setSelectedIsMixed(false); }}
                            title={internalThread.title || "Team ENERLEFT"}
                            lastAt={internalThread.last_message_at}
                            dataTestId="comms-thread-internal"
                            logoUrl={null}
                        />
                    )}
                </div>

                <div className="lt-comms__section">
                    <div className="lt-comms__section-h">Conversazioni</div>
                    {(index.internal_dms || []).length === 0 ? (
                        <div className="lt-comms__empty">Nessuna conversazione. Avvia con il pulsante &quot;Nuova&quot; in alto.</div>
                    ) : (
                        (index.internal_dms || [])
                            .filter((dm) => !search.trim() || (dm.title || "").toLowerCase().includes(search.trim().toLowerCase()))
                            .map((dm) => (
                                <ThreadRow
                                    key={dm.id}
                                    active={selectedId === dm.id && !selectedIsMixed}
                                    onClick={() => { setSelectedId(dm.id); setSelectedIsMixed(false); }}
                                    title={dm.title || "Conversazione"}
                                    lastAt={dm.last_message_at}
                                    unread={dm.unread}
                                    logoUrl={null}
                                    dataTestId={`comms-dm-${dm.id}`}
                                />
                            ))
                    )}
                </div>

                <div className="lt-comms__section">
                    <div className="lt-comms__section-h">Con clienti</div>
                    {(index.mixed_dms || []).length === 0 ? (
                        <div className="lt-comms__empty">Nessuna conversazione con clienti.</div>
                    ) : (
                        (index.mixed_dms || [])
                            .filter((dm) => !search.trim() || (dm.title || "").toLowerCase().includes(search.trim().toLowerCase()))
                            .map((dm) => (
                                <ThreadRow
                                    key={dm.id}
                                    active={selectedId === dm.id && selectedIsMixed}
                                    onClick={() => { setSelectedId(dm.id); setSelectedIsMixed(true); }}
                                    title={dm.title || "Conversazione"}
                                    lastAt={dm.last_message_at}
                                    unread={dm.unread}
                                    logoUrl={null}
                                    dataTestId={`comms-mixed-dm-${dm.id}`}
                                />
                            ))
                    )}
                </div>

                <div className="lt-comms__section">
                    <div className="lt-comms__section-h">Società clienti</div>
                    {filteredClients.length === 0 && (
                        <div className="lt-comms__empty">Nessuna società corrisponde alla ricerca.</div>
                    )}
                    {filteredClients.map((t) => (
                        <ThreadRow
                            key={t.id}
                            active={selectedId === t.id && !selectedIsMixed}
                            onClick={() => { setSelectedId(t.id); setSelectedIsMixed(false); }}
                            title={t.client?.company_name}
                            lastAt={t.last_message_at}
                            unread={t.internal_unread}
                            logoUrl={t.client?.logo_url}
                            dataTestId={`comms-thread-${t.id}`}
                        />
                    ))}
                </div>
            </aside>

            {/* RIGHT — chat */}
            <section className="lt-comms__chat" data-testid="comms-chat">
                {!thread ? (
                    <div className="lt-comms__placeholder">Seleziona una conversazione.</div>
                ) : (
                    <>
                        <header className="lt-comms__chat-head" data-testid="comms-chat-head">
                            {thread.type === "team" ? (
                                <div className="flex items-center gap-3">
                                    <div className="lt-comms__chat-logo lt-comms__chat-logo--team">TE</div>
                                    <div>
                                        <div className="lt-comms__chat-title">{thread.title || "Team ENERLEFT"}</div>
                                        <div className="lt-comms__chat-sub">
                                            Canale condiviso
                                            {Array.isArray(thread.participants) && thread.participants[0]?.count
                                                ? ` · ${thread.participants[0].count} partecipanti` : ""}
                                        </div>
                                    </div>
                                </div>
                            ) : thread.type === "internal_dm" || thread.type === "dm" ? (
                                (() => {
                                    const parts = thread.participants || [];
                                    const others = parts.filter((p) => (p.user_id || p.id) !== me?.id);
                                    const isGroup = parts.length >= 3;
                                    const isMixed = thread.type === "dm";
                                    const initials = !isGroup && others[0]?.name
                                        ? others[0].name.split(/\s+/).map((s) => s[0]).slice(0,2).join("").toUpperCase()
                                        : null;
                                    return (
                                        <div className="flex items-center gap-3">
                                            <div className="lt-comms__chat-logo lt-comms__chat-logo--dm">
                                                {isGroup ? <Users size={18} strokeWidth={1.75} /> : <span>{initials}</span>}
                                            </div>
                                            <div>
                                                <div className="lt-comms__chat-title">{thread.title || "Conversazione"}</div>
                                                <div className="lt-comms__chat-sub">
                                                    {isMixed
                                                        ? (isGroup ? `${parts.length} partecipanti · misto` : "Conversazione con cliente")
                                                        : (isGroup ? `${parts.length} partecipanti` : "Conversazione privata")}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })()
                            ) : (
                                <div className="flex items-center gap-3">
                                    <div className="lt-comms__chat-logo">
                                        {thread.client?.logo_url
                                            ? <img src={thread.client.logo_url} alt="" />
                                            : <span>{(thread.client?.company_name || "?").slice(0, 1).toUpperCase()}</span>}
                                    </div>
                                    <div>
                                        <div className="lt-comms__chat-title">{thread.client?.company_name || "Cliente"}</div>
                                        <div className="lt-comms__chat-sub">Conversazione con il cliente</div>
                                    </div>
                                </div>
                            )}
                        </header>

                        <div className="lt-comms__chat-body" data-testid="comms-chat-body">
                            {(thread.messages || []).length === 0 ? (
                                <div className="lt-comms__empty-chat">Ancora nessun messaggio. Inizia tu la conversazione.</div>
                            ) : (
                                (thread.messages || []).map((m) => (
                                    <ChatMessage key={m.id} m={m} viewerIsAuthor={m.author?.user_type === "internal" && m.author?.user_id === me?.id} />
                                ))
                            )}
                            <div ref={threadEndRef} />
                        </div>

                        <form className="lt-comms__composer" onSubmit={send} data-testid="comms-composer">
                            <textarea
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                onKeyDown={onComposerKey}
                                placeholder="Scrivi un messaggio…"
                                rows={2}
                                data-testid="comms-composer-text"
                            />
                            <button type="submit" className="lt-btn-primary" disabled={!text.trim() || sending} data-testid="comms-composer-send">
                                <Send size={14} /> Invia
                            </button>
                        </form>
                    </>
                )}
            </section>

            {newConvOpen && (
                <div className="lt-modal-backdrop" onClick={() => setNewConvOpen(false)} data-testid="comms-new-conv-modal">
                    <div className="lt-modal" onClick={(e) => e.stopPropagation()} style={{ width: "min(540px, 92vw)" }}>
                        <div className="lt-modal__head">
                            <h2 className="lt-modal__title">Nuova conversazione</h2>
                            <button className="lt-icon-btn" onClick={() => setNewConvOpen(false)} aria-label="Chiudi"><X size={16} /></button>
                        </div>
                        <div style={{ padding: 20 }}>
                            <p style={{ color: "#5F6560", fontSize: 13.5, margin: "0 0 14px" }}>Seleziona uno o più destinatari.</p>
                            <div style={{ position: "relative", marginBottom: 12 }}>
                                <Search size={14} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#8A8F88" }} />
                                <input
                                    type="search"
                                    className="lt-input"
                                    placeholder="Cerca per nome o email…"
                                    value={convQuery}
                                    onChange={(e) => setConvQuery(e.target.value)}
                                    style={{ paddingLeft: 36, width: "100%" }}
                                    data-testid="comms-new-conv-search"
                                />
                            </div>
                            <div style={{ maxHeight: 320, overflowY: "auto", border: "1px solid #E3E5E1", borderRadius: 10 }} data-testid="comms-new-conv-users">
                                {recipientSections === null ? (
                                    <div style={{ padding: 16, color: "#8A8F88", fontSize: 13 }}>Caricamento…</div>
                                ) : recipientSections.length === 0 ? (
                                    <div style={{ padding: 16, color: "#8A8F88", fontSize: 13 }}>Nessun destinatario disponibile.</div>
                                ) : recipientSections.map((section) => {
                                    const q = convQuery.trim().toLowerCase();
                                    const users = (section.users || []).filter((u) =>
                                        !q || (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q)
                                    );
                                    if (users.length === 0) return null;
                                    return (
                                        <div key={section.key} data-testid={`comms-recipient-section-${section.key}`}>
                                            <div style={{
                                                padding: "10px 14px 6px",
                                                fontFamily: '"IBM Plex Mono", monospace',
                                                fontSize: 11,
                                                letterSpacing: "0.06em",
                                                textTransform: "uppercase",
                                                color: "#8A8F88",
                                                background: "#FAFAF7",
                                                borderTop: "1px solid #EEEFEA",
                                                borderBottom: "1px solid #EEEFEA",
                                            }}>{section.label}</div>
                                            {users.map((u) => {
                                                const key = `${u.user_type}:${u.user_id}`;
                                                const checked = convSelected.some((s) => s.user_type === u.user_type && s.user_id === u.user_id);
                                                const initials = (u.name || "").split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase();
                                                return (
                                                    <label key={key} className="lt-user-row" data-testid={`comms-new-conv-user-${u.user_id}`}>
                                                        <span className={"lt-checkbox" + (checked ? " is-checked" : "")}>
                                                            <input
                                                                type="checkbox"
                                                                checked={checked}
                                                                onChange={(e) => {
                                                                    setConvSelected((prev) => e.target.checked
                                                                        ? [...prev, { user_type: u.user_type, user_id: u.user_id, name: u.name }]
                                                                        : prev.filter((x) => !(x.user_type === u.user_type && x.user_id === u.user_id))
                                                                    );
                                                                }}
                                                            />
                                                            {checked && <Check size={12} color="#181B20" strokeWidth={3} />}
                                                        </span>
                                                        <span className="lt-avatar-initials">{initials || "?"}</span>
                                                        <span style={{ flex: 1, minWidth: 0 }}>
                                                            <span style={{ display: "block", color: "#181B20", fontSize: 14, fontWeight: 500 }}>{u.name}</span>
                                                            <span style={{ display: "block", color: "#5F6560", fontSize: 12 }}>
                                                                {u.user_type === "internal" ? (u.role || "staff") : (u.client_name || "cliente")} · {u.email}
                                                            </span>
                                                        </span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    );
                                })}
                            </div>
                            <div style={{ marginTop: 12 }}>
                                <div style={{ fontSize: 13, color: "#5F6560", marginBottom: 6 }}>Selezionati: {convSelected.length}</div>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                    {convSelected.map((s) => (
                                        <span key={`${s.user_type}:${s.user_id}`} className="lt-pill" data-testid={`comms-new-conv-chip-${s.user_id}`}>
                                            {s.name}
                                            <button type="button" onClick={() => setConvSelected((prev) => prev.filter((x) => !(x.user_type === s.user_type && x.user_id === s.user_id)))} aria-label="Rimuovi">
                                                <X size={12} />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="lt-form-actions" style={{ padding: "12px 20px" }}>
                            <div style={{ flex: 1 }} />
                            <button className="lt-btn-secondary" onClick={() => { setNewConvOpen(false); setConvSelected([]); setConvQuery(""); }} data-testid="comms-new-conv-cancel">Annulla</button>
                            <button
                                className="lt-btn-primary"
                                disabled={convSelected.length === 0 || creatingConv}
                                onClick={async () => {
                                    setCreatingConv(true);
                                    try {
                                        const allInternal = convSelected.every((s) => s.user_type === "internal");
                                        let created;
                                        if (allInternal) {
                                            const { data } = await api.post("/internal/threads/internal-dm", {
                                                participant_ids: convSelected.map((s) => s.user_id),
                                            });
                                            created = { id: data.id, isMixed: false, reused: data.reused };
                                        } else {
                                            const { data } = await api.post("/comunicazioni/dm", {
                                                participants: convSelected.map((s) => ({ user_type: s.user_type, user_id: s.user_id })),
                                            });
                                            created = { id: data.id, isMixed: true, reused: data.reused };
                                        }
                                        toast.success(created.reused ? "Conversazione già esistente aperta" : "Conversazione creata");
                                        setNewConvOpen(false);
                                        setConvSelected([]);
                                        setConvQuery("");
                                        await loadIndex(true);
                                        setSelectedId(created.id);
                                        setSelectedIsMixed(created.isMixed);
                                    } catch (err) {
                                        toast.error(err?.response?.data?.detail || "Errore creazione conversazione");
                                    } finally {
                                        setCreatingConv(false);
                                    }
                                }}
                                data-testid="comms-new-conv-submit"
                            >
                                {creatingConv ? "Creazione…" : "Crea conversazione"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
